/**
 * Dependency Injection Container
 * 
 * Implements the Inversion of Control pattern to manage dependencies
 * and provide loose coupling between components.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

type Constructor<T = object> = new (...args: unknown[]) => T;
type ServiceFactory<T> = () => T;
type ServiceIdentifier<T> = string | symbol | Constructor<T>;

interface ServiceRegistration<T> {
  factory: ServiceFactory<T>;
  singleton: boolean;
  instance?: T;
}

/**
 * Dependency Injection Container
 * 
 * Manages service registration, resolution, and lifecycle.
 * Supports both singleton and transient service lifetimes.
 */
export class DIContainer {
  private services = new Map<ServiceIdentifier<unknown>, ServiceRegistration<unknown>>();

  /**
   * Register a service with the container
   */
  public register<T>(
    identifier: ServiceIdentifier<T>,
    factory: ServiceFactory<T>,
    options: { singleton?: boolean } = { singleton: true }
  ): this {
    this.services.set(identifier, {
      factory,
      singleton: options.singleton ?? true,
    });
    return this;
  }

  /**
   * Register a singleton service
   */
  public registerSingleton<T>(
    identifier: ServiceIdentifier<T>,
    factory: ServiceFactory<T>
  ): this {
    return this.register(identifier, factory, { singleton: true });
  }

  /**
   * Register a transient service (new instance each time)
   */
  public registerTransient<T>(
    identifier: ServiceIdentifier<T>,
    factory: ServiceFactory<T>
  ): this {
    return this.register(identifier, factory, { singleton: false });
  }

  /**
   * Register a class constructor
   */
  public registerClass<T>(
    identifier: ServiceIdentifier<T>,
    constructor: Constructor<T>,
    options: { singleton?: boolean } = { singleton: true }
  ): this {
    return this.register(
      identifier,
      () => new constructor(),
      options
    );
  }

  /**
   * Register a value as a singleton
   */
  public registerValue<T>(identifier: ServiceIdentifier<T>, value: T): this {
    this.services.set(identifier, {
      factory: () => value,
      singleton: true,
      instance: value,
    });
    return this;
  }

  /**
   * Resolve a service from the container
   */
  public resolve<T>(identifier: ServiceIdentifier<T>): T {
    const registration = this.services.get(identifier);
    
    if (!registration) {
      throw new Error(`Service not registered: ${String(identifier)}`);
    }

    // Return existing singleton instance
    if (registration.singleton && registration.instance) {
      return registration.instance as T;
    }

    // Create new instance
    const instance = registration.factory();

    // Store singleton instance
    if (registration.singleton) {
      registration.instance = instance;
    }

    return instance as T;
  }

  /**
   * Check if a service is registered
   */
  public has<T>(identifier: ServiceIdentifier<T>): boolean {
    return this.services.has(identifier);
  }

  /**
   * Remove a service registration
   */
  public unregister<T>(identifier: ServiceIdentifier<T>): boolean {
    return this.services.delete(identifier);
  }

  /**
   * Clear all registrations
   */
  public clear(): void {
    this.services.clear();
  }

  /**
   * Get all registered service identifiers
   */
  public getRegisteredServices(): Array<ServiceIdentifier<unknown>> {
    return Array.from(this.services.keys());
  }
}

// Export singleton container instance
export const container = new DIContainer();

/**
 * Convenience getter methods for commonly used services
 */
export const get = <T>(identifier: ServiceIdentifier<T>): T => {
  return container.resolve(identifier);
}; 